import { useEffect, useMemo, useState } from 'react';
import {
  needsTermsAcceptance,
  type TermsAcceptanceProfile,
} from '../core/rules/termsAcceptance';
import { CURRENT_TERMS_VERSION } from '../core/legal/termsVersion';
import { acceptTermsOfService } from '../services/firebase/FunctionsService';
import { usesFirebaseAuth } from './authSession';
import { listenUserDoc } from './listenUserDoc';
import { useFailOpenTimeout } from './useFailOpenTimeout';
import type { AppUser } from '../types';

export async function submitTermsAcceptance(version = CURRENT_TERMS_VERSION) {
  return acceptTermsOfService({ version });
}

function termsMillis(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (value && typeof value === 'object' && 'toMillis' in value) {
    const toMillis = (value as { toMillis?: () => number }).toMillis;
    if (typeof toMillis === 'function') return toMillis.call(value);
  }
  return null;
}

function profileFromUserDoc(data: Record<string, unknown> | null): TermsAcceptanceProfile | null {
  if (!data) return null;
  return {
    requiresTermsAcceptance: data.requiresTermsAcceptance === true,
    termsAcceptedAt: termsMillis(data.termsAcceptedAt),
    termsVersion: typeof data.termsVersion === 'string' ? data.termsVersion : null,
  };
}

export function useTermsAcceptance(user: AppUser | null) {
  const firebaseAuth = usesFirebaseAuth();
  const [profile, setProfile] = useState<TermsAcceptanceProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!firebaseAuth || !user?.id) {
      setProfile(null);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    return listenUserDoc(
      user.id,
      (data) => {
        setProfile(profileFromUserDoc(data));
        setLoading(false);
      },
      () => {
        setProfile(null);
        setLoading(false);
      },
    );
  }, [firebaseAuth, user?.id]);

  const timedOut = useFailOpenTimeout(Boolean(firebaseAuth && user?.id));
  const waiting = loading && !timedOut;

  const shouldShow = useMemo(() => needsTermsAcceptance({
    firebaseAuth,
    profile,
    profileLoaded: !waiting,
  }), [firebaseAuth, profile, waiting]);

  return { loading: waiting, shouldShow, profile };
}
